// src/components/blog/BlogErrorBoundary.js
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { isChunkLoadError, handleChunkError } from '../../utils/chunkRetry';

/**
 * Wrapper component to provide route params to the error boundary
 */
const BlogErrorBoundaryWrapper = ({ children }) => {
  const { countryCode } = useParams();
  return (
    <BlogErrorBoundary countryCode={countryCode}>
      {children}
    </BlogErrorBoundary>
  );
};

/**
 * Specialized error boundary for blog components that handles chunk loading errors
 * and provides a better user experience for blog-specific errors
 */
class BlogErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      isChunkError: false,
      retryCount: 0
    };
  }
  
  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error);
    
    // For chunk loading errors, we'll handle them specially
    if (isChunkError) {
      return { hasError: true, error, isChunkError: true };
    }
    
    return { hasError: true, error, isChunkError: false };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error("Blog error caught:", error, errorInfo);
    
    // Handle chunk loading errors
    if (isChunkLoadError(error)) {
      console.warn("Blog chunk loading error detected");
      
      // If this is the first chunk error, try to reload automatically
      if (this.state.retryCount === 0) {
        console.log("Attempting automatic retry for chunk loading error");
        setTimeout(() => {
          this.setState(prevState => ({ 
            hasError: false, 
            error: null, 
            isChunkError: false,
            retryCount: prevState.retryCount + 1
          }));
        }, 1000);
        return;
      }
      
      // If retry failed, reload the page
      handleChunkError(error);
      return;
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      isChunkError: false,
      retryCount: this.state.retryCount + 1
    });
  };

  handleBackToBlog = () => {
    // Navigate back to blog list
    const { countryCode } = this.props;
    window.location.href = `/country/${countryCode || 'canada'}/blog`;
  };
  
  render() {
    if (this.state.hasError) {
      const { countryCode } = this.props;
      
      // If it's a chunk loading error, show a loading-style message
      if (this.state.isChunkError) {
        return (
          <div style={{ 
            padding: '40px 20px', 
            textAlign: 'center',
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #3b82f6',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', color: '#374151' }}>
              Loading blog post...
            </h3>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280', fontSize: '16px', maxWidth: '400px' }}>
              We're preparing your article. This usually just takes a moment.
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        );
      }
      
      // For other errors, show a user-friendly blog error page
      return (
        <div style={{ 
          padding: '40px 20px', 
          textAlign: 'center',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ 
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              color: '#374151', 
              marginBottom: '16px',
              fontWeight: '600'
            }}>
              Oops! Something went wrong
            </h2>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              We're having trouble loading this blog post. Don't worry - our blog posts are still here!
            </p>
            
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={this.handleRetry}
                style={{ 
                  padding: '12px 24px', 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Try Again
              </button>
              
              <Link 
                to={`/country/${countryCode || 'canada'}/blog`}
                style={{ 
                  padding: '12px 24px', 
                  backgroundColor: '#f3f4f6', 
                  color: '#374151', 
                  border: 'none', 
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: '500',
                  display: 'inline-block'
                }}
              >
                ← Back to Blog
              </Link>
            </div>
            
            {process.env.NODE_ENV === 'development' && (
              <details style={{ 
                marginTop: '24px', 
                textAlign: 'left',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '16px'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: '500', color: '#dc2626' }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{ 
                  color: '#dc2626', 
                  fontSize: '12px',
                  overflow: 'auto',
                  marginTop: '12px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {this.state.error && this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    
    return this.props.children; 
  }
}

export default BlogErrorBoundaryWrapper;
